import { Injectable, signal } from '@angular/core';
import { Geolocation, PermissionStatus } from '@capacitor/geolocation';

export interface GeoCoords {
  lat: number;
  lng: number;
}

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {
  public coords = signal<GeoCoords | null>(null);
  public permissionStatus = signal<PermissionStatus['location'] | null>(null);

  private watchId: string | null = null;

  async requestAndWatch(): Promise<void> {
    const status = await Geolocation.requestPermissions();
    this.permissionStatus.set(status.location);

    if (status.location !== 'granted') {
      return;
    }

    await this.startWatch();
  }

  async checkPermission(): Promise<void> {
    const status = await Geolocation.checkPermissions();
    this.permissionStatus.set(status.location);

    if (status.location === 'granted') {
      await this.startWatch();
    }
  }

  private async startWatch(): Promise<void> {
    if (this.watchId) {
      return;
    }

    try {
      const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true });
      this.coords.set({
        lat: position.coords.latitude,
        lng: position.coords.longitude
      });
    } catch {
      // ignore — coords remain null
    }

    this.watchId = await Geolocation.watchPosition(
      { enableHighAccuracy: true },
      (position, err) => {
        if (err || !position) return;
        this.coords.set({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      }
    );
  }

  stopWatch(): void {
    if (this.watchId) {
      Geolocation.clearWatch({ id: this.watchId });
      this.watchId = null;
    }
  }

  distanceTo(lat: number, lng: number): number {
    const coords = this.coords();
    if (!coords) return Infinity;

    const R = 6371;
    const dLat = this.toRad(lat - coords.lat);
    const dLng = this.toRad(lng - coords.lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(coords.lat)) *
        Math.cos(this.toRad(lat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  private toRad(value: number): number {
    return (value * Math.PI) / 180;
  }
}
